import { cx } from "@emotion/css";
import type { AxiosError } from "axios";
import config from "config";
import { useFormik } from "formik";
import {
	ArrowRight,
	Gift,
	Hourglass,
	LogOut,
	User,
	UserRound,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	PrimaryButton,
	TextBlackButton,
} from "src/components/Default/LekUI/Button";
import { FormTextField } from "src/components/Default/LekUI/TextField";
import {
	Modal,
	ModalHeader,
} from "src/components/Modals/Components/ModalsComponents";
import { ProfileSections } from "src/components/Profile/Profile";
import { useFontSize } from "src/hooks/useFontSize";
import useIsAuthPage from "src/hooks/useIsAuthPage";
import {
	useActivePromocode,
	useGetUserInfo,
	useLogout,
} from "src/hooks/useUser";
import * as Yup from "yup";

import { isIos } from "../../../capacitor/appsHelpers";
import {
	hasAccessToLekExams,
	hasAccessToPesExams,
	hasLimitedAccess,
} from "../../../helpers/getPermissions";
import isPes from "../../../helpers/isPes";

const VoucherForm = () => {
	const { t } = useTranslation();
	const { modalHeaderFontSizeClass } = useFontSize();
	const { mutateAsync, isPending: isLoading } = useActivePromocode();
	const activate = mutateAsync as unknown as (values: {
		promocode: string;
	}) => Promise<void>;

	const { submitForm, values, errors, setFieldValue } = useFormik({
		initialValues: { promocode: "" },
		validationSchema: Yup.object().shape({
			promocode: Yup.string().required(t("errors.emptySingleField")),
		}),
		onSubmit: async (values, { setFieldError }) => {
			try {
				await activate(values);
			} catch (err: unknown) {
				if ((err as AxiosError).response?.status === 404) {
					setFieldError("promocode", t("errors.wrongPromocode"));
				} else {
					setFieldError("promocode", t("errors.somethingWentWrong"));
				}
			}
		},
	});

	return (
		<div className="w-100 flex flex-col gap-8 p-16 border-light-grey border-radius-12 bg-grey">
			<label
				htmlFor="payment-required-voucher"
				className={cx(modalHeaderFontSizeClass, "font-bold text-black")}
			>
				{t("modals.paymentRequired.voucherCode", "Kod vouchera")}
			</label>
			<div className="flex items-start gap-8">
				<FormTextField
					id="payment-required-voucher"
					data-test-id="payment-required-voucher-input"
					margin="none"
					fullWidth
					placeholder={t(
						"modals.paymentRequired.voucherPlaceholder",
						"Wpisz kod",
					)}
					value={values.promocode}
					error={errors.promocode}
					onChange={(e) => setFieldValue("promocode", e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && submitForm()}
				/>
				<PrimaryButton
					data-test-id="payment-required-voucher-submit"
					className="btn-size-big"
					isLoading={isLoading}
					onClick={submitForm}
				>
					{t("modals.paymentRequired.activate", "Aktywuj")}
				</PrimaryButton>
			</div>
		</div>
	);
};

export function PaymentRequiredModal() {
	const { t } = useTranslation();
	const { mutateAsync: logout, isPending: isLoggingOut } = useLogout();
	const isAuthPage = useIsAuthPage();
	const { isLoading, data: user, isError } = useGetUserInfo(!isAuthPage);
	const [voucherOpen, setVoucherOpen] = useState(false);
	const [profileOpen, setProfileOpen] = useState(false);

	if (!user || isLoading || isError) {
		return null;
	}

	const hasAccess = isPes() ? hasAccessToPesExams() : hasAccessToLekExams();
	if (hasAccess || hasLimitedAccess()) {
		return null;
	}

	const goToPricing = () => {
		if (isPes()) {
			const specId = user.selectedSpecialisation?.id;
			location.href = specId
				? `${config.urls.pricingPes}${specId}`
				: config.urls.pricingPes;
			return;
		}
		location.href = config.urls.pricing;
	};

	// Profil w modalu — bez dostępu nie wypuszczamy usera na inne strony.
	if (profileOpen) {
		return (
			<Modal maxWidth="md" onClose={() => setProfileOpen(false)}>
				<div className="flex flex-col gap-24">
					<ModalHeader
						Icon={UserRound}
						title={t("userAccount.profile.mainTitle")}
						hasCloseButton
					/>
					<ProfileSections />
				</div>
			</Modal>
		);
	}

	return (
		<Modal>
			<div className="payment-required-modal">
				<ModalHeader
					Icon={Hourglass}
					title={t(
						"modals.paymentRequired.header",
						"Nie masz aktywnego dostępu",
					)}
					description={t(
						"modals.paymentRequired.subheader",
						"Wybierz plan dopasowany do siebie i zacznij naukę – albo wróć do niej bez utraty postępów.",
					)}
				/>

				{!isIos() && (
					<PrimaryButton
						data-test-id="payment-required-choose-plan"
						fullWidth
						className="payment-required-modal--primary"
						endIcon={<ArrowRight className="icon-small" />}
						onClick={goToPricing}
					>
						{t("modals.paymentRequired.primary", "Wybierz plan")}
					</PrimaryButton>
				)}

				<div className="payment-required-modal--tiles mt--8">
					{!isIos() && (
						<button
							type="button"
							data-test-id="payment-required-voucher-toggle"
							aria-expanded={voucherOpen}
							className={cx(
								"payment-required-modal--tile border-light-grey border-radius-12 bg-white text-black cursor-pointer",
								voucherOpen && "item-selected",
							)}
							onClick={() => setVoucherOpen((open) => !open)}
						>
							<span className="payment-required-modal--tile-icon payment-required-modal--tile-icon-orange">
								<Gift className="icon-small icon-error" />
							</span>
							{t("modals.paymentRequired.voucher", "Mam voucher")}
						</button>
					)}
					<button
						type="button"
						data-test-id="payment-required-go-to-profile"
						className="payment-required-modal--tile border-light-grey border-radius-12 bg-white text-black cursor-pointer"
						onClick={() => setProfileOpen(true)}
					>
						<span className="payment-required-modal--tile-icon">
							<User className="icon-small icon-primary" />
						</span>
						{t("modals.paymentRequired.profile", "Przejdź do profilu")}
					</button>
				</div>

				{voucherOpen && <VoucherForm />}

				<div className="divider divider--horizontal" />

				<TextBlackButton
					data-test-id="payment-required-logout"
					className="align-self-center"
					startIcon={<LogOut className="icon-small" />}
					isLoading={isLoggingOut}
					onClick={() => logout()}
				>
					{t("defaultsText.logout")}
				</TextBlackButton>
			</div>
		</Modal>
	);
}
